/**
 * Scrolling Content
 * Pans/zooms the pinned image to the chapter currently in the middle of the
 * screen, highlights its marker, and reveals chapters as they scroll in.
 * "The screen" is the area below the sticky header (--sc-top, which follows
 * the theme's live --header-height).
 * Video links (Play, Trailer, film cover, Introduction button) open in the
 * section's lightbox instead of leaving the page.
 */
if (!customElements.get('scrolling-content')) {
	class ScrollingContent extends HTMLElement {
		connectedCallback() {
			this.setupLightbox();
			this.canvas = this.querySelector('[data-canvas]');
			this.caption = this.querySelector('[data-caption]');
			this.chapters = Array.from(this.querySelectorAll('[data-chapter]'));
			this.markers = Array.from(this.querySelectorAll('[data-marker]'));
			if (!this.canvas || !this.chapters.length) return;

			this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			this.active = null;

			this.onMarkerClick = this.onMarkerClick.bind(this);
			this.onBlockSelect = this.onBlockSelect.bind(this);
			this.onResize = this.onResize.bind(this);
			this.markers.forEach((marker) => marker.addEventListener('click', this.onMarkerClick));
			document.addEventListener('shopify:block:select', this.onBlockSelect);

			if ('IntersectionObserver' in window) {
				this.observeActive();
				window.addEventListener('resize', this.onResize, { passive: true });

				this.revealObserver = new IntersectionObserver((entries) => {
					entries.forEach((entry) => {
						if (!entry.isIntersecting) return;
						entry.target.classList.add('is-visible');
						this.revealObserver.unobserve(entry.target);
					});
				}, { rootMargin: '0px 0px -12% 0px' });

				this.chapters.forEach((chapter) => this.revealObserver.observe(chapter));
				this.classList.add('is-ready');
			}

			// Recalculate the pan when the image area changes size.
			if ('ResizeObserver' in window) {
				this.resizeObserver = new ResizeObserver(() => this.applyView());
				this.resizeObserver.observe(this.canvas);
			}

			this.setActive(this.chapters[0]);
		}

		disconnectedCallback() {
			this.removeEventListener('click', this.onVideoClick);
			if (this.lightboxObserver) this.lightboxObserver.disconnect();
			// The theme moves the modal to <body>; remove it with the section so
			// theme-editor re-renders don't leave copies behind.
			if (this.lightbox) this.lightbox.remove();
			if (this.activeObserver) this.activeObserver.disconnect();
			if (this.revealObserver) this.revealObserver.disconnect();
			if (this.resizeObserver) this.resizeObserver.disconnect();
			window.removeEventListener('resize', this.onResize);
			document.removeEventListener('shopify:block:select', this.onBlockSelect);
			(this.markers || []).forEach((marker) => marker.removeEventListener('click', this.onMarkerClick));
		}

		setupLightbox() {
			const id = this.dataset.lightbox;
			if (!id) return;
			// Keep only the newest modal with this id (older copies come from
			// theme-editor re-renders).
			const modals = document.querySelectorAll(`[id="${id}"]`);
			modals.forEach((modal, index) => {
				if (index < modals.length - 1) modal.remove();
			});
			this.lightbox = modals[modals.length - 1];
			if (!this.lightbox) return;
			this.lightboxMedia = this.lightbox.querySelector('[data-sc-lightbox-media]');
			this.lightboxDialog = this.lightbox.querySelector('[role="dialog"]');

			this.onVideoClick = this.onVideoClick.bind(this);
			this.addEventListener('click', this.onVideoClick);

			// Stop the video and return focus when the theme's modal closes.
			this.lightboxObserver = new MutationObserver(() => {
				if (this.lightbox.hasAttribute('open')) return;
				if (this.lightboxMedia) this.lightboxMedia.replaceChildren();
				if (this.videoOpener) {
					this.videoOpener.focus({ preventScroll: true });
					this.videoOpener = null;
				}
			});
			this.lightboxObserver.observe(this.lightbox, { attributes: true, attributeFilter: ['open'] });
		}

		onVideoClick(event) {
			const link = event.target.closest('[data-sc-video]');
			if (!link || !this.contains(link) || !this.lightbox) return;
			// Unknown link types, or the theme's modal not loaded yet: follow the link.
			if (typeof this.lightbox.show !== 'function') return;
			const video = this.getVideo(link.getAttribute('href'));
			if (!video) return;
			event.preventDefault();
			this.openVideo(video, link);
		}

		// Turns a YouTube, Vimeo or video-file link into something playable here.
		getVideo(href) {
			let url;
			try {
				url = new URL(href, window.location.href);
			} catch (error) {
				return null;
			}
			const host = url.hostname.replace(/^(www|m)\./, '');
			const path = url.pathname;

			let youtubeId = null;
			if (host === 'youtu.be') {
				youtubeId = path.split('/')[1];
			} else if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
				const match = path.match(/\/(?:embed|shorts|live)\/([\w-]+)/);
				youtubeId = url.searchParams.get('v') || (match && match[1]);
			}
			if (youtubeId) {
				const start = parseInt(url.searchParams.get('t') || url.searchParams.get('start'), 10);
				return {
					type: 'iframe',
					src: `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&playsinline=1&rel=0${start ? `&start=${start}` : ''}`
				};
			}

			if (host.endsWith('vimeo.com')) {
				const match = path.match(/(?:\/video)?\/(\d+)(?:\/([\da-f]+))?/);
				if (match) {
					const hash = match[2] || url.searchParams.get('h');
					return {
						type: 'iframe',
						src: `https://player.vimeo.com/video/${match[1]}?autoplay=1${hash ? `&h=${hash}` : ''}`
					};
				}
			}

			if (/\.(mp4|webm|mov|m4v)$/i.test(path)) {
				return { type: 'video', src: url.href };
			}
			return null;
		}

		openVideo(video, opener) {
			const title = opener.dataset.scTitle || opener.getAttribute('aria-label') || 'Video';
			let media;
			if (video.type === 'iframe') {
				media = document.createElement('iframe');
				media.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
				media.allowFullscreen = true;
				media.referrerPolicy = 'strict-origin-when-cross-origin';
			} else {
				media = document.createElement('video');
				media.controls = true;
				media.autoplay = true;
				media.playsInline = true;
			}
			media.src = video.src;
			media.title = title;
			this.lightboxMedia.replaceChildren(media);
			if (this.lightboxDialog) this.lightboxDialog.setAttribute('aria-label', title);

			this.videoOpener = opener;
			this.lightbox.show(opener);
			// The modal fades in from visibility: hidden, so wait until it can take
			// focus (lets Escape close it and keeps keyboard users inside).
			const close = this.lightbox.querySelector('[id^="ModalClose-"]');
			let frames = 0;
			const focusClose = () => {
				if (!close || !this.lightbox.hasAttribute('open')) return;
				close.focus({ preventScroll: true });
				if (document.activeElement !== close && frames++ < 30) setTimeout(focusClose, 16);
			};
			setTimeout(focusClose, 16);
		}

		// Height of the sticky header the pinned layer sits under.
		getNavOffset() {
			return parseFloat(getComputedStyle(this).getPropertyValue('--sc-top')) || 0;
		}

		// The chapter crossing the middle band of the visible area (below the
		// sticky header) is "active". Margins are in px, so rebuild on resize.
		observeActive() {
			if (this.activeObserver) this.activeObserver.disconnect();
			const top = this.getNavOffset();
			const visible = Math.max(1, window.innerHeight - top);
			const band = Math.round(visible * 0.45);
			this.activeObserver = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) this.setActive(entry.target);
				});
			}, { rootMargin: `-${Math.round(top + band)}px 0px -${band}px 0px` });
			this.chapters.forEach((chapter) => this.activeObserver.observe(chapter));
		}

		onResize() {
			cancelAnimationFrame(this.resizeFrame);
			this.resizeFrame = requestAnimationFrame(() => this.observeActive());
		}

		setActive(chapter) {
			if (!chapter || chapter === this.active) return;
			this.active = chapter;
			const id = chapter.dataset.chapter;
			// The Introduction is an overview: whole image, every marker visible.
			this.classList.toggle('is-overview', chapter.hasAttribute('data-overview'));
			// An Introduction with its background turned off hides the content shade.
			this.classList.toggle('is-plain', chapter.hasAttribute('data-no-bg'));

			this.markers.forEach((marker) => {
				const on = marker.dataset.marker === id;
				marker.classList.toggle('is-active', on);
				if (on) marker.setAttribute('aria-current', 'true');
				else marker.removeAttribute('aria-current');
			});
			if (this.caption) this.caption.textContent = chapter.dataset.caption || '';
			this.applyView();
		}

		// Centre the chapter's location at its zoom level, without letting the
		// image edges pull inside its frame.
		applyView() {
			if (!this.active) return;
			const zoom = Math.max(1, parseFloat(this.active.dataset.zoom) || 1);
			const w = this.canvas.offsetWidth;
			const h = this.canvas.offsetHeight;
			const percent = (value) => (Number.isFinite(parseFloat(value)) ? parseFloat(value) : 50) / 100;
			const x = percent(this.active.dataset.x) * w;
			const y = percent(this.active.dataset.y) * h;
			const clamp = (value, min) => Math.min(0, Math.max(min, value));
			const tx = clamp(w / 2 - x * zoom, w - w * zoom);
			const ty = clamp(h / 2 - y * zoom, h - h * zoom);

			this.canvas.style.transform = `translate(${tx}px, ${ty}px) scale(${zoom})`;
			this.canvas.style.setProperty('--sc-marker-scale', (1 / zoom).toFixed(3));
		}

		scrollToChapter(chapter) {
			// Centre the chapter in the area below the sticky header (or align its
			// top just under the header when it is taller than that area).
			chapter.classList.add('is-visible');
			const top = this.getNavOffset();
			const visible = window.innerHeight - top;
			// Layout position (offsetTop ignores the reveal slide-in transform).
			let pageTop = 0;
			for (let el = chapter; el; el = el.offsetParent) pageTop += el.offsetTop;
			const height = chapter.offsetHeight;
			const offset = height < visible ? (visible - height) / 2 : 24;
			window.scrollTo({
				top: pageTop - top - offset,
				behavior: this.reducedMotion ? 'auto' : 'smooth'
			});
			this.setActive(chapter);
		}

		onMarkerClick(event) {
			const id = event.currentTarget.dataset.marker;
			const chapter = this.chapters.find((el) => el.dataset.chapter === id);
			if (chapter) this.scrollToChapter(chapter);
		}

		// Theme editor: selecting a Chapter or Athlete block jumps to its chapter.
		onBlockSelect(event) {
			if (!this.contains(event.target)) return;
			const chapter = event.target.closest('[data-chapter]');
			if (chapter) this.scrollToChapter(chapter);
		}
	}

	customElements.define('scrolling-content', ScrollingContent);
}
